import React from 'react';
import {
  makeStyles,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  Color20Regular,
  Settings20Regular,
  Eye20Regular,
  Send20Regular,
  DataHistogram20Regular,
  ProjectionScreen20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  ribbon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
    width: '100%',
    padding: '0 12px',
    gap: '6px',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  iconButton: {
    height: '44px',
    padding: '10px 12px',
    gap: '4px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    backgroundColor: 'transparent',
    border: 'none',
    minWidth: 'auto',
    borderRadius: '0',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      color: '#03787C',
      '& svg': {
        color: '#03787C',
      },
    },
  },
  primaryButton: {
    height: '32px',
    borderRadius: '24px',
    background: 'linear-gradient(90deg, #2F9E9E 0%, #2D5F99 100%)',
    color: 'white',
    border: 'none',
    padding: '0 12px',
    gap: '4px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    '&:hover': {
      background: 'linear-gradient(90deg, #1D6363 0%, #1E3F66 100%)',
      color: 'white',
    },
  },
  outlineButton: {
    height: '32px',
    borderRadius: '24px',
    backgroundColor: 'white',
    color: '#03787C',
    border: '1px solid #03787C',
    padding: '0 12px',
    gap: '4px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    '&:hover': {
      backgroundColor: 'rgba(16, 124, 128, 0.05)',
    },
  },
  actionButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  moreButton: {
    height: '44px',
    width: '44px',
    padding: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    minWidth: 'auto',
    borderRadius: '0',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
});

export interface FormsRibbonProps {
  onViewResponsesClick?: () => void;
}

const FormsRibbon: React.FC<FormsRibbonProps> = ({ onViewResponsesClick }) => {
  const styles = useStyles();

  return (
    <div className={styles.ribbon}>
      {/* Left Section - Style, Settings, Preview */}
      <div className={styles.leftSection}>
        <Button
          appearance="subtle"
          icon={<Color20Regular />}
          className={styles.iconButton}
        >
          Style
        </Button>
        <Button
          appearance="subtle"
          icon={<Settings20Regular />}
          className={styles.iconButton}
        >
          Settings
        </Button>
        <Button
          appearance="subtle"
          icon={<Eye20Regular />}
          className={styles.iconButton}
        >
          Preview
        </Button>
      </div>

      {/* Action Buttons - Collect responses, View responses */}
      <div className={styles.actionButtonsContainer}>
        <Button
          icon={<Send20Regular />}
          className={styles.primaryButton}
        >
          Collect responses
        </Button>
        <Button
          icon={<DataHistogram20Regular />}
          className={styles.outlineButton}
          onClick={onViewResponsesClick}
        >
          View responses
        </Button>
      </div>

      {/* Right Section - Present, More */}
      <div className={styles.buttonGroup}>
        <Button
          appearance="subtle"
          icon={<ProjectionScreen20Regular />}
          className={styles.iconButton}
        >
          Present
        </Button>
        <Menu>
          <MenuTrigger>
            <Button
              appearance="subtle"
              icon={<MoreHorizontal20Regular />}
              className={styles.moreButton}
              aria-label="More options"
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem>Print</MenuItem>
              <MenuItem>Export</MenuItem>
              <MenuItem>Share</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  );
};

export default FormsRibbon;
